import { Injectable, Injector } from '@angular/core';
import { Platform } from '@angular/cdk/platform';
import { BehaviorSubject, distinctUntilChanged, filter, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';

import { Experience } from '@hiptraveler/data-access/api';
import { ChatCompletionsService } from '@hiptraveler/core/openai';
import { AppListenerService, getWindowRef, isWidget } from '@hiptraveler/common';
import { AllowScroll, FormCarouselData, FormValue, ScreenView, ScreenViewValue, SummaryItems } from '.';
import { syncRemovedSummaryItems, newFormValue } from './summary-items';
import { environment } from '@hiptraveler/environments';

export const FORM_VALUE: FormValue = {
  dateRange:    { start: new Date, end: new Date },
  carouselData: { screen3: [], screen4: [], screen5: [], screen6: [] },
  clientInfo:   { adults: 0, children: 0, infants: 0 }
};

@Injectable()
export class EFAStateServiceService extends ChatCompletionsService {

  scrollToScreen$$  = new Subject<number>();
  scrollToScreen$  = this.scrollToScreen$$.asObservable();

  scrollToView$$  = new Subject<'next' | 'previous'>();
  scrollToView$  = this.scrollToView$$.asObservable();
  
  inputPending$$ = new BehaviorSubject<boolean>(false);
  inputPending$ = this.inputPending$$.asObservable();
  
  allowScroll$$ = new BehaviorSubject<AllowScroll>('allow');
  allowScroll$ = this.allowScroll$$.asObservable().pipe(distinctUntilChanged());
  
  scrolling$$ = new BehaviorSubject<boolean>(false);
  scrolling$ = this.scrolling$$.asObservable();
  
  panelCloseTrigger$$ = new Subject<void>();
  panelCloseTrigger$ = this.panelCloseTrigger$$.asObservable();

  brandHeaderTypeState$$ = new Subject<boolean>();
  welcomeViewTypeState$$ = new Subject<boolean>();
  pauseBrandInfoUXState$$ = new Subject<boolean>();
  formSubmit$ = new Subject<void>();
  formSubmitCompleted$ = new Subject<void>();

  subscription$ = new Subject<void>();

  // ScreenViewValue

  get view(): ScreenViewValue {
    const desktop = getWindowRef().innerWidth > 600;
    return {
      screen1: 0,
      screen2: desktop ? 400*1 : (getWindowRef().innerHeight*1 - 58),
      screen3: desktop ? 400*2 : (getWindowRef().innerHeight*2 - 58*2),
      screen4: desktop ? 400*3 : (getWindowRef().innerHeight*3 - 58*3),
      screen5: desktop ? 400*4 : (getWindowRef().innerHeight*4 - 58*4),
      screen6: desktop ? 400*5 : (getWindowRef().innerHeight*5 - 58*5),
      screen7: desktop ? 400*6 : (getWindowRef().innerHeight*6 - 58*6),
      screen8: desktop ? 400*7 : (getWindowRef().innerHeight*7 - 58*7),
      screen9: desktop ? 400*8 : (getWindowRef().innerHeight*8 - 58*8),
      screen10: desktop ? 400*9 : (getWindowRef().innerHeight*9 - 58*9),
      screen11: desktop ? 400*10 : (getWindowRef().innerHeight*10 - 58*1),
      screen12: desktop ? 400*11 : (getWindowRef().innerHeight*11 - 58*1)
    };
  }

  // screenViewValue

  inputMessage$$ = new BehaviorSubject<string>('');
  inputMessage$ = this.inputMessage$$.asObservable().pipe( filter(Boolean), distinctUntilChanged() );

  // summaryTags

  summaryItems$$ = new BehaviorSubject<SummaryItems>(null);
  summaryItems$ = this.summaryItems$$.asObservable().pipe(
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );
  summaryItemsPending$ = this.summaryItems$$.asObservable().pipe(map(e => e === null));

  // screenViewValue

  screenViewValue$$ = new BehaviorSubject<ScreenView | null>(null);
  screenViewValue$ = this.screenViewValue$$.asObservable().pipe(filter(Boolean));
  
  // Aspect ratio

  isMobile$ = this.appListener.clientWidth$.pipe( map(e => e <= (isWidget() ? 600 : 700)) );
  isDesktop$ = this.isMobile$.pipe( map(e => !e) );

  get isMobile(): boolean {
    return getWindowRef().innerWidth <= (isWidget() ? 600 : 700);
  }
  get isDesktop(): boolean {
    return !this.isMobile;
  }

  get timeout(): Record<'scrollTop' | 'scrolling', number> {
    const mobilePlatform = this.platform.IOS || this.platform.ANDROID;
    const isMobile = this.isMobile && mobilePlatform && !environment.local;
    return {
      scrollTop: isMobile ? 100 : 400,
      scrolling: isMobile ? 300 : 1200
    }
  }

  // locations
  
  locations$$ = new BehaviorSubject<Partial<Experience>[] | undefined>(undefined);
  locations$ = this.locations$$.asObservable();

  appendLocation(location: Partial<Experience>): void {
    this.locations$$.next([ location, ...(this.locations$$.value || []) ]);
  }

  // formValue

  formValue$$ = new BehaviorSubject<FormValue>(FORM_VALUE);
  formValue$ = this.formValue$$.asObservable().pipe(
    map(e => JSON.stringify(e)), distinctUntilChanged(), map(e => JSON.parse(e) as FormValue)
  );
  carouselData$ = this.formValue$$.asObservable().pipe(map(e => e.carouselData));

  get formValue(): FormValue {
    return this.formValue$$.value;
  }

  patchFormValue(form: Partial<FormValue> | null): void {
    const value = { ...this.formValue$$.value };
    this.formValue$$.next({ ...value,  ...(form || {}) });
  }

  patchCarouselItems(carouselData: Partial<FormCarouselData>): void {
    const formValue = { ...this.formValue$$.value };
    const form = {
      ...formValue, carouselData: { ...formValue.carouselData, ...carouselData }
    };
    this.formValue$$.next({ ...formValue, ...form });
  }
  
  constructor(
    private platform: Platform,
    private appListener: AppListenerService,
    injector: Injector
  ) {
    super(injector);
  }

  getInputMessageByScreen(screen: ScreenView): Observable<string> {
    return this.inputMessage$.pipe(
      switchMap((inputMessage: string) => of(this.screenViewValue$$.value).pipe(
        filter(e => e === screen),
        map(() => inputMessage)
      )),
      takeUntil(this.subscription$)
    );
  }

  removeSummaryItem(index: number): void {

    const data = this.summaryItems$$.value;
    if (!data) return;
    const targetItem = data[index];
    const formValue = newFormValue(targetItem, this.formValue);
    this.patchFormValue(formValue);

    syncRemovedSummaryItems({
      data, index, formValue: this.formValue
    }, this.patchFormValue.bind(this))

    const summaryItems = [ ...data ];
    summaryItems.splice(index, 1);
    this.summaryItems$$.next(summaryItems);
  }

}
